import React from 'react';
import { CSSTransition as RTGCSSTransition } from 'react-transition-group';

// Wrapper around react-transition-group's CSSTransition that forces a reflow
// between adding enter/appear class and enter-active/appear-active class.
//
// With nodeRef, RTG skips findDOMNode (which implicitly forced a reflow).
// Without a reflow, the browser applies both classes in the same frame,
// causing the CSS transition to not fire (element jumps to final value).
//
// The onEnter/onExit callbacks fire AFTER RTG adds the base class but
// BEFORE RTG's setState callback adds the -active class. Forcing a reflow
// here ensures the browser computes the starting opacity before the
// transition-triggering -active class is applied.

function forceReflow(node) {
	if (node) {
		// Reading offsetHeight forces the browser to flush pending style changes
		// and perform a full layout reflow. This is stronger than getComputedStyle()
		// which only forces style resolution. On slower hardware, getComputedStyle()
		// can be optimized away by the browser, but offsetHeight requires layout
		// computation which guarantees the starting CSS class (e.g. opacity: 0.01)
		// is fully applied before the -active class triggers the transition.
		// eslint-disable-next-line no-unused-expressions
		node.offsetHeight;
	}
}

function CSSTransition({ onEnter, onExit, nodeRef, ...props }) {
	const handleEnter = (...args) => {
		// With nodeRef, RTG passes (appearing) not (node, appearing).
		// args[0] is a boolean, NOT a DOM node — always use nodeRef.
		forceReflow(nodeRef?.current);
		if (onEnter) onEnter(...args);
	};

	const handleExit = (...args) => {
		forceReflow(nodeRef?.current);
		if (onExit) onExit(...args);
	};

	return (
		<RTGCSSTransition
			onEnter={handleEnter}
			onExit={handleExit}
			nodeRef={nodeRef}
			{...props}
		/>
	);
}

export { CSSTransition };
